/** @format */

export function normalizeName(str) {
	if (!str) return ''
	return (
		String(str)
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim()
			.split(/\s+/)[0] || ''
	)
}

export function filterIndividuals(principals) {
	if (!Array.isArray(principals)) return []
	return principals.filter((p) => p && p.subjectType === 'Individuals')
}

export function needsDisambiguation(principal) {
	if (!principal) return false

	const given = typeof principal.givenName === 'string' ? principal.givenName.trim() : ''
	const family = typeof principal.familyName === 'string' ? principal.familyName.trim() : ''
	if (given || family) return false

	const full = typeof principal.fullName === 'string' ? principal.fullName.trim() : ''
	if (!full) return false

	const tokens = full.split(/\s+/).filter(Boolean)
	return tokens.length >= 2
}

export function deriveNameParts(principal) {
	if (!principal) return { first_name: '', last_name: '' }

	const given = typeof principal.givenName === 'string' ? principal.givenName.trim() : ''
	const family = typeof principal.familyName === 'string' ? principal.familyName.trim() : ''

	if (given || family) {
		return { first_name: given, last_name: family }
	}

	const full = typeof principal.fullName === 'string' ? principal.fullName.trim() : ''
	if (!full) return { first_name: '', last_name: '' }

	const tokens = full.split(/\s+/).filter(Boolean)
	if (tokens.length === 0) return { first_name: '', last_name: '' }
	if (tokens.length === 1) return { first_name: '', last_name: tokens[0] }
	return { first_name: tokens.slice(1).join(' '), last_name: tokens[0] }
}

function normalizedLastName(str) {
	if (!str) return ''
	return String(str)
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim()
}

export function toIsoDateOnly(value) {
	if (!value) return null
	if (value instanceof Date) {
		if (isNaN(value.getTime())) return null
		const y = value.getFullYear()
		const m = String(value.getMonth() + 1).padStart(2, '0')
		const d = String(value.getDate()).padStart(2, '0')
		return `${y}-${m}-${d}`
	}
	const str = String(value)
	if (str.length >= 10) return str.slice(0, 10)
	return null
}

export function buildCountryIdByIsoMap(countryIsoByCountryId = {}) {
	const map = {}
	Object.entries(countryIsoByCountryId).forEach(([countryId, iso]) => {
		if (iso) map[String(iso).toUpperCase()] = Number(countryId)
	})
	return map
}

function datesEqual(a, b) {
	const aIso = toIsoDateOnly(a)
	const bIso = toIsoDateOnly(b)
	if (!aIso || !bIso) return false
	return aIso === bIso
}

function principalIsoCountry(principal) {
	const nationality = Array.isArray(principal?.nationality) ? principal.nationality : []
	const iso = nationality[0]?.isoAlpha2Code
	return iso ? String(iso).toUpperCase() : null
}

function lastNameScore(token, savedLast) {
	if (!token || !savedLast) return 0
	if (token === savedLast) return 50
	if (token.includes(savedLast) || savedLast.includes(token)) return 20
	return 0
}

function firstNamePrefixMatches(token, savedFirst) {
	if (!token || !savedFirst || token.length < 3 || savedFirst.length < 3) return false
	return token.slice(0, 3) === savedFirst.slice(0, 3)
}

function computeNameMatch(dnbPrincipal, savedFirst, savedLast) {
	const given = typeof dnbPrincipal.givenName === 'string' ? dnbPrincipal.givenName.trim() : ''
	const family = typeof dnbPrincipal.familyName === 'string' ? dnbPrincipal.familyName.trim() : ''
	const full = typeof dnbPrincipal.fullName === 'string' ? dnbPrincipal.fullName.trim() : ''
	const tokens = full
		.split(/\s+/)
		.filter(Boolean)
		.map((t) => normalizedLastName(t))

	if (family || (given && tokens.length < 2)) {
		const dnbFirst = normalizeName(given)
		const dnbLast = normalizedLastName(family)
		const last = lastNameScore(dnbLast, savedLast)
		const first = firstNamePrefixMatches(dnbFirst, savedFirst) ? 30 : 0
		return {
			score: last + first,
			hasExactLastName: Boolean(dnbLast && savedLast && dnbLast === savedLast),
		}
	}

	if (tokens.length === 0) return { score: 0, hasExactLastName: false }

	let bestScore = 0
	for (let i = 0; i < tokens.length; i++) {
		const last = lastNameScore(tokens[i], savedLast)
		let first = 0
		for (let j = 0; j < tokens.length; j++) {
			if (j === i) continue
			if (firstNamePrefixMatches(tokens[j], savedFirst)) {
				first = 30
				break
			}
		}
		bestScore = Math.max(bestScore, last + first)
	}

	const hasExactLastName = Boolean(savedLast && tokens.some((t) => t === savedLast))
	return { score: bestScore, hasExactLastName }
}

export function scoreMatch(dnbPrincipal, savedPerson, { countryIsoByCountryId = {} } = {}) {
	if (!dnbPrincipal || !savedPerson) return 0

	const savedFirst = normalizeName(savedPerson.first_name)
	const savedLast = normalizedLastName(savedPerson.last_name)
	const { score: nameScore } = computeNameMatch(dnbPrincipal, savedFirst, savedLast)

	let score = nameScore

	const birthDateMatch = datesEqual(dnbPrincipal.birthDate, savedPerson.date_of_birth)
	if (birthDateMatch) {
		score += 40
	}

	const dnbIso = principalIsoCountry(dnbPrincipal)
	const savedIso = countryIsoByCountryId[savedPerson.country_id]
	const countryMatch = Boolean(dnbIso && savedIso && String(savedIso).toUpperCase() === dnbIso)
	if (countryMatch) {
		score += 10
	}

	return score
}

export function getConfidenceTier(
	score,
	{ hasExactNameMatch = false, hasCorroboratingField = false } = {},
) {
	if (score >= 90 && hasExactNameMatch && hasCorroboratingField) return 'confirmed'
	if (score >= 50 && hasExactNameMatch) return 'likely'
	if (score >= 20) return 'possible'
	return 'none'
}

export function findMatches(dnbPrincipal, savedPeople, ctx = {}) {
	if (!Array.isArray(savedPeople) || savedPeople.length === 0) return []
	const { countryIsoByCountryId = {} } = ctx

	const dnbIso = principalIsoCountry(dnbPrincipal)

	const scored = savedPeople.map((person) => {
		const score = scoreMatch(dnbPrincipal, person, { countryIsoByCountryId })
		const savedFirst = normalizeName(person.first_name)
		const savedLast = normalizedLastName(person.last_name)
		const { hasExactLastName: hasExactNameMatch } = computeNameMatch(
			dnbPrincipal,
			savedFirst,
			savedLast,
		)
		const hasCorroboratingField =
			datesEqual(dnbPrincipal?.birthDate, person.date_of_birth) ||
			Boolean(
				dnbIso &&
				countryIsoByCountryId[person.country_id] &&
				String(countryIsoByCountryId[person.country_id]).toUpperCase() === dnbIso,
			)

		const tier = getConfidenceTier(score, { hasExactNameMatch, hasCorroboratingField })
		return { person, score, tier }
	})

	return scored.filter((m) => m.tier !== 'none').sort((a, b) => b.score - a.score)
}
